import * as Sentry from '@sentry/vue';
import { App } from 'vue';
import { Router } from 'vue-router';

interface CapturedSentryEvent {
   exception?: {
      values?: Array<{ type?: string }>;
   };
   extra?: Record<string, unknown>;
}

// Power BI dispatches a bubbling error CustomEvent when a visual fails to render.
// Sentry's onerror handler records that as an unhandled exception.
function isPowerBiEmbedErrorEvent(event: CapturedSentryEvent): boolean {
   const exceptionType = event.exception?.values?.[0]?.type;
   if (exceptionType !== 'CustomEvent') return false;

   const serialized = event.extra?.__serialized__;
   if (!serialized || typeof serialized !== 'object') return false;

   const { target, type } = serialized as { target?: unknown; type?: unknown };
   return type === 'error' && typeof target === 'string' && target.includes('reporting__frame');
}

function discardPowerBiEmbedError<TEvent extends CapturedSentryEvent>(event: TEvent): TEvent | null {
   if (isPowerBiEmbedErrorEvent(event)) return null;
   return event;
}

function initSentry(app: App, router: Router) {
   if (!['acc', 'prod'].includes(import.meta.env.VITE_APP_ENV)) return;

   const env = import.meta.env.VITE_APP_ENV;
   const dsn = import.meta.env.VITE_APP_SENTRY_DSN;
   const baseUrl = import.meta.env.VITE_APP_BASE_URL;
   const version = import.meta.env.VITE_APP_VERSION;

   Sentry.init({
      app,
      attachProps: true,
      attachStacktrace: true,
      dsn,
      environment: env,
      beforeSend: discardPowerBiEmbedError,
      ignoreErrors: [
         'AxiosError',
         'Network Error',
         'Request aborted',
         'Request canceled due to new request.',
         /Failed to fetch dynamically imported module: .*/,
         /error loading dynamically imported module: .*/,
         /\[404 ERR_BAD_REQUEST\] - \/people\/\d+\/approvals\/ask/,
         /\[500 ERR_BAD_RESPONSE\] - .*/,
      ],
      integrations: [
         Sentry.breadcrumbsIntegration({ console: true, fetch: true, history: true, sentry: true, xhr: true }),
         Sentry.browserTracingIntegration({
            router,
            tracingOrigins: [baseUrl, /^\//],
         }),
         Sentry.replayIntegration(),
         // Sentry.feedbackIntegration({
         //    colorScheme: 'system',
         // }), tis nie wel voor derest
      ],
      logErrors: true,
      release: version,
      replaysOnErrorSampleRate: 0.5,
      replaysSessionSampleRate: 0.5,
      trackComponents: true,
      tracesSampleRate: 1.0,
   });

   Sentry.setContext('env', { origin: import.meta.env.VITE_APP_BASE_URL, mode: import.meta.env.VITE_APP_ENV });
}

export { discardPowerBiEmbedError, initSentry };
